/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Hyunsu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Tue Jul 16 2024
 **/

import React, { useCallback, useEffect, useState, useRef } from "react";
import { debounce, uniqBy } from "lodash-es";
import styled from "@emotion/styled";
import { useShallow } from "zustand/react/shallow";

import { useChatMessageActions, useChatRoomActions, useSpaceChannelState } from "@/frameworks/core/chat";
import {
	Button,
	Column,
	Icon,
	Row,
	Tab,
	TabContent,
	TabItem,
	Typography,
	TypographyTooltip,
	useModal,
	useTab,
	Menu,
	useToast
} from "@/frameworks/designStuff/components";
import { Maybe } from "@/frameworks/libraries/components";
import { useEventHandler } from "@rfice/eventhandler";
import { _T } from "@rfice/language";
import { EventID, GROUP_POPOVER_ID, ModalID, ZIndex } from "@/frameworks/core/commons";
import { useFullSizeChatManager, usePopover, useRoomViewActions } from "@/frameworks/core/hooks";
import { useChat } from "@/frameworks/core/API";
import { colors } from "@/frameworks/core/styles";
import { useStompClient } from "@/frameworks/libraries/network";
import { Configure } from "@/frameworks/libraries/utilities";
import { hoverScrollCss } from "@/frameworks/designStuff/utils";
import { Properties } from "@/frameworks/libraries/properties";
import { MessageInput, MessageList, VoiceCallLayer } from "@/modules/chat/components/Common";
import { useChatDataStore } from "@/modules/chat/store";

import { UnauthorizedChannel } from "../../../../Commons";
import { SpaceChannelOption } from "../SpaceChannelOption";
import { CreateSpaceChannel } from "../CreateSpaceChannel";
import * as S from "../../../emotion";
import { DeletedChannel } from "../../../../Commons/DeletedChannel";
import { MessageSearchInConversation, MessageSearchResult } from "../../../../MiniChatPopup/components";
import { SearchedResultWrapper } from "./private/emotion";

export default function FullSizeChatContent({
	spaceID,
	roomID,
	onChangeSpace,
	activityMessageID
}: {
	spaceID: string;
	roomID: string;
	onChangeSpace: (id: string) => void;
	activityMessageID?: string;
}) {
	const modal = useModal();
	const toast = useToast();
	const chatRoomActions = useChatRoomActions();
	const chatAPI = useChat();
	const popover = usePopover();
	const [isUpdateChannelName, setIsUpdateChannelName] = useState(false);
	const [changeRoomID, setChangeRoomID] = useState("");
	const { currentId, handleSelectTab, handleItemScroll } = useTab();
	const [currentID, setCurrentID] = useState(currentId);
	const tabContainerRef = useRef<HTMLDivElement>(null);
	const [isShowMore, setIsShowMore] = useState(false);

	const spaceInfo = useSpaceChannelState(spaceID);
	const spaceRoomList = uniqBy(spaceInfo?.rooms, "roomId");
	const { idFullSizeChat, setIdFullSizeChat } = useChatDataStore(
		useShallow((state) => ({
			idFullSizeChat: state.fullsize_chat_id,
			setIdFullSizeChat: state.set_fullsize_chat_id
		}))
	);

	const { updateRoomView } = useRoomViewActions();
	const { fullSizeChatActions } = useFullSizeChatManager();

	const currentRoom = spaceInfo?.rooms.find((room) => room.roomId === roomID);
	const [channelName, setChannelName] = useState(currentRoom?.roomName ?? "");

	const channelUserList = currentRoom?.users.map((user) => ({
		name: user.name,
		src: user.profileImagePath,
		userID: user.id
	}));

	useEffect(() => {
		const currentRoom = spaceInfo?.rooms.find((room) => room.roomId === roomID);
		setChannelName(currentRoom?.roomName ?? "");
	}, [currentRoom]);

	useEffect(() => {
		if (idFullSizeChat && idFullSizeChat !== currentId) {
			updateRoomView({
				roomId: idFullSizeChat as string,
				deviceKey: Properties.browserTabID,
				view: false
			});
			fullSizeChatActions.set({ channelID: currentId });
		}
		setIdFullSizeChat(currentId);
		setCurrentID(currentId);
		onChangeSpace(currentId);
	}, [currentId]);

	const checkTabsOverflow = () => {
		const containerWidth = tabContainerRef?.current?.offsetWidth || 0;
		const tabsWidth = tabContainerRef?.current?.querySelector("ul")?.offsetWidth || 0;
		setIsShowMore(tabsWidth > containerWidth);
	};

	useEffect(() => {
		if (!tabContainerRef.current) return;

		const observer = new ResizeObserver(() => {
			checkTabsOverflow();
		});

		observer.observe(tabContainerRef.current);
		return () => {
			if (!tabContainerRef.current) return;
			observer.unobserve(tabContainerRef.current);
		};
	}, []);

	useEffect(() => {
		handleSelectTab(String(roomID));
		const list = spaceRoomList.filter(
			(room) => !((room.private && room.deleted && !room.roomJoined) || (!room.private && room.deleted))
		);
		const idxRoom = list.findIndex((item) => item.roomId === roomID);
		if (idxRoom > 0) {
			handleItemScroll({
				scrollTargetID: "full-space-chat",
				type: "select",
				index: idxRoom
			});
		}
	}, [roomID]);

	const handleMoreClick = async (evt: React.MouseEvent<HTMLButtonElement>) => {
		const { channel = "" } = evt.currentTarget.dataset;
		const { currentTarget } = evt;
		evt.stopPropagation();
		const { data } = (await chatAPI.getRoom.execute({ roomID: channel })) as {
			data: WebServices.Chat.GetRoom.Data;
		};
		if (!data) return;
		const { adminCount, isCurrentUserAdmin } = data.users.reduce(
			(acc, user) => {
				if (user.spaceAuthority === "SPACE_ADMIN") {
					acc.adminCount++;
					if (user.id === Configure.userID) {
						acc.isCurrentUserAdmin = true;
					}
				}
				return acc;
			},
			{ adminCount: 0, isCurrentUserAdmin: false }
		);
		popover.show({
			popoverID: GROUP_POPOVER_ID.SPACE_CHAT_SUB_MENU,
			uniqueID: "channel_alarm",
			content: (
				<SpaceChannelOption
					variant="fullSize"
					spaceID={data.spaceId!}
					isOnlyAdmin={adminCount === 1 && isCurrentUserAdmin}
					notificationType={data.notificationType}
					roomID={data.roomId}
					roomType={data.general ? "general" : data.private ? "private" : "public"}
					users={data.users}
				/>
			),
			anchorEl: currentTarget,
			direction: {
				x: "right",
				y: "bottom"
			},
			position: {
				x: -150,
				y: 10
			},
			zIndex: ZIndex.POPUP + 1
		});
	};

	useEventHandler(EventID.UPDATE_CHANNEL_NAME, (roomID: string) => {
		setChangeRoomID(roomID);
		setIsUpdateChannelName(true);
	});

	useEventHandler(EventID.CHANGE_FULL_SIZE_CHAT_TAB, (roomID: string) => {
		setCurrentID(roomID);
	});

	const handleChangeChannelName = async () => {
		if (!channelName) {
			const roomName = spaceRoomList.find((space) => space.roomId === roomID)?.roomName ?? "";
			setChannelName(roomName);
			if (isUpdateChannelName) setIsUpdateChannelName(false);
			return;
		}
		chatRoomActions.spaceChat.rename({
			spaceId: spaceID,
			roomId: currentID || "",
			name: channelName,
			events: {
				onSuccess() {
					setIsUpdateChannelName(false);
				}
			}
		});
	};

	useStompClient(
		{
			onMessageReceived(message) {
				const messageType = message.type as ChatServices.MessageIDs | PushServices.User.MessageIDs;
				switch (messageType) {
					case "memberLeft":
					case "roomDeleted": {
						const { roomId, leftMemberId, senderId } = message.payload;
						if (
							senderId === Configure.userID ||
							!spaceInfo?.rooms.filter((room) => room.roomId === roomId)?.[0]?.private ||
							leftMemberId === Configure.userID
						) {
							setCurrentID(spaceInfo?.rooms[0]?.roomId || "");
							handleSelectTab(String(spaceInfo?.rooms[0]?.roomId));
							handleItemScroll({
								scrollTargetID: "full-space-chat",
								type: "delete"
							});
						}

						if (currentID === roomId) {
							const validRooms = spaceRoomList.filter((room) => !room.deleted).map((room) => room.roomId);
							const newID = validRooms[0] || "";
							setCurrentID(newID);
							handleSelectTab(newID);
						}

						updateRoomView({
							roomId: roomId as string,
							deviceKey: Properties.browserTabID,
							view: false
						});
						const roomDelete = spaceInfo?.rooms.find((room) => room.roomId === roomId);
						if (messageType === "roomDeleted" && !roomDelete?.private) {
							toast.show({
								type: "system",
								message: _T("{{roomName}} 이 채널은 삭제되었습니다.", {
									roomName: roomDelete?.roomName
								})
							});
						}

						break;
					}
					case "roomCreated": {
						handleSelectTab(message.payload.roomInfo.roomId);
						setCurrentID(message.payload.roomInfo.roomId);
						handleItemScroll({
							scrollTargetID: "full-space-chat",
							type: "create"
						});
						updateRoomView({
							roomId: message.payload.roomInfo.roomId as string,
							deviceKey: Properties.browserTabID,
							view: true
						});
						break;
					}
				}
			}
		},
		[currentId]
	);

	const handleClickShowMore = (e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		popover.show<GROUP_POPOVER_ID>({
			popoverID: GROUP_POPOVER_ID.SPACE_CHAT_SUB_MENU,
			uniqueID: "spaceSubMenu",
			anchorEl: e.currentTarget,
			direction: {
				x: "right",
				y: "bottom"
			},
			position: {
				x: -480,
				y: 4
			},
			content: (
				<Menu.Group
					id="space-chat-list"
					css={[{ width: "480px", maxHeight: "336px", padding: "8px", overflowY: "auto" }, hoverScrollCss()]}
					onClick={(e) => e.stopPropagation()}
				>
					<Menu.List>
						{spaceRoomList.map((room, index) => {
							return (
								<S.MenuItem
									id={room.roomId}
									key={room.roomId}
									focus={room.roomId === currentId}
									onClick={() => {
										handleSelectTab(String(room.roomId));
										handleItemScroll({
											scrollTargetID: "full-space-chat",
											type: "select",
											index
										});
										popover.hide(GROUP_POPOVER_ID.SPACE_CHAT_SUB_MENU);
									}}
								>
									<TypographyTooltip.p
										tooltipProps={{ hasIcon: false }}
										componentProps={{
											maxLine: 1,
											ellipsis: true,
											stretch: true,
											typoColor: "neutral_80_100"
										}}
									>
										{room.roomName}
									</TypographyTooltip.p>
								</S.MenuItem>
							);
						})}
					</Menu.List>
				</Menu.Group>
			)
		});
	};

	return (
		<>
			<div
				css={{
					width: "100%",
					display: "flex",
					paddingLeft: "16px",
					paddingRight: "16px",
					borderBottom: "1px solid #878B9333"
				}}
			>
				<div css={{ overflow: "hidden" }}>
					<Tab id="full-space-chat" ref={tabContainerRef} style={{ padding: 0, borderBottom: "none" }}>
						{spaceRoomList.map((room) => {
							if (room.private && room.deleted && !room.roomJoined) return;
							if (!room.private && room.deleted) return;
							return (
								<TabItem
									id={room.roomId}
									key={room.roomId}
									onClick={() => {
										handleSelectTab(room.roomId);
										updateRoomView({
											roomId: room.roomId,
											deviceKey: Properties.browserTabID,
											view: true
										});
									}}
								>
									<S.TabItemBox horizonAlign="center" verticalAlign="center" gap={4}>
										<Maybe
											test={room.roomId === changeRoomID && isUpdateChannelName}
											fallback={
												<Maybe
													test={room.roomId === changeRoomID}
													fallback={
														<TypographyTooltip.p
															tooltipProps={{ hasIcon: false }}
															componentProps={{
																maxLine: 1,
																ellipsis: true,
																style: {
																	marginLeft: "4px",
																	marginRight: "4px",
																	maxWidth: "200px"
																},
																stretch: true,
																typoColor:
																	room.roomId === currentID
																		? "neutral_80_100"
																		: "neutral_80_60"
															}}
														>
															{room.roomName}
														</TypographyTooltip.p>
													}
												>
													<Typography typoSize="Title3">{channelName}</Typography>
												</Maybe>
											}
										>
											<input
												style={{
													marginLeft: "4px",
													maxWidth: "200px",
													border: `1px solid ${colors.neutral_30_100}`,
													borderRadius: 10,
													padding: "2px 12px",
													backgroundColor: colors.neutral_00_100
												}}
												autoFocus
												maxLength={50}
												placeholder={room.roomName}
												value={channelName}
												onClick={(ev) => {
													ev.stopPropagation();
												}}
												onChange={(ev) => {
													setChannelName(ev.target.value);
												}}
												onKeyDown={(ev) => {
													if (ev.key === "Enter") {
														handleChangeChannelName();
													}
												}}
												onBlur={() => {
													handleChangeChannelName();
												}}
											/>
										</Maybe>

										{room.private ? (
											<Icon
												name="Lock24"
												size="18px"
												fill={
													room.roomId === currentID
														? colors.neutral_80_100
														: colors.neutral_80_60
												}
											/>
										) : null}
										<Maybe
											test={room.private && room.deleted && room.roomJoined}
											fallback={
												<Maybe test={room.roomId === currentID}>
													<Button.Icon
														name="More24"
														size="medium_18"
														variant="text"
														data-channel-type={
															room.roomName === "General"
																? "general"
																: room.private
																	? "private"
																	: "public"
														}
														data-space={room.spaceId}
														data-chatkind="Space"
														data-channel={room.roomId}
														data-name={room.roomName}
														data-space-authority={spaceInfo?.spaceAuthority}
														data-creator={room.createdBy}
														onClick={handleMoreClick}
													/>
												</Maybe>
											}
										/>
									</S.TabItemBox>
								</TabItem>
							);
						})}
					</Tab>
				</div>

				<Row horizonAlign="center" verticalAlign="center" style={{ marginLeft: "12px" }}>
					{isShowMore && (
						<Button.Icon
							name="Right24"
							size="medium"
							variant="text"
							style={{ marginRight: "12px" }}
							onClick={handleClickShowMore}
						/>
					)}
					<Maybe test={spaceInfo?.spaceType === "NORMAL"}>
						<Button.Icon
							name="Add24"
							size="medium"
							variant="text"
							onClick={() =>
								modal.show({
									id: ModalID.CREATE_SPACE_CHANNEL,
									content: (
										<CreateSpaceChannel
											spaceID={spaceInfo?.spaceId || ""}
											channelUserList={channelUserList || []}
											onHide={() => {
												modal.hide(ModalID.CREATE_SPACE_CHANNEL);
											}}
											onCreateSpaceSuccess={(id) => {
												onChangeSpace(id);
											}}
										/>
									)
								})
							}
						/>
					</Maybe>
				</Row>
			</div>

			<Column css={{ width: "100%", height: "100%", overflow: "hidden" }}>
				{spaceRoomList.map((channelData) => {
					if (channelData.deleted && !channelData.roomJoined) return;
					return (
						<SpaceChatTab
							currentTabID={currentID || ""}
							key={channelData.roomId}
							channelData={channelData}
							spaceID={spaceID}
							roomID={channelData.roomId}
							activityMessageID={activityMessageID}
						/>
					);
				})}
			</Column>
		</>
	);
}

function SpaceChatTab({
	channelData,
	roomID,
	spaceID,
	currentTabID,
	activityMessageID
}: {
	channelData: WebServices.SpaceChat.Inquiry.Data["rooms"][number];
	roomID: string;
	spaceID: string;
	currentTabID: string;
	activityMessageID?: string;
}) {
	const { messageReaded } = useChatMessageActions();

	const handleShowRecentMessage = useCallback(
		debounce((message) => {
			if (message.isMe) return;
			if (message.roomMessageId === channelData.messageLastReadId) return;
			messageReaded({ channelID: roomID, messageID: message.roomMessageId });
		}, 1000),
		[channelData?.messageLastReadId]
	);

	const [isShowMessageSearchResult, setIsShowMessageSearchResult] = useState(false);
	const [searchText, setSearchText] = useState<string>("");
	const [isSearchInputOpened, setIsSearchInputOpened] = useState(false);

	useEventHandler(
		EventID.TOGGLE_OPEN_SEARCH_ON_CONVERSATION_INPUT,
		({ roomId: targetRoomId }: { roomId: string }) => {
			if (targetRoomId !== roomID) return;
			setIsSearchInputOpened((prev) => !prev);
			setIsShowMessageSearchResult(Boolean(searchText && !isSearchInputOpened));
		},
		[roomID, searchText, isSearchInputOpened]
	);

	useEventHandler(
		EventID.OPEN_SEARCH_RESULT_ON_MINI_CHAT,
		({
			searchValue,
			isOpenSearchResult,
			roomId: targetRoomId
		}: {
			searchValue: string;
			isOpenSearchResult: boolean;
			roomId: string;
		}) => {
			if (targetRoomId !== roomID) return;
			if (isOpenSearchResult) {
				setIsShowMessageSearchResult(Boolean(searchValue));
				if (!searchValue || searchValue.trim().length === 0) return;
				setSearchText(searchValue);
			} else {
				setSearchText("");
				setIsShowMessageSearchResult(false);
			}
		},
		[roomID]
	);

	const handleOnClearSearchInput = () => {
		setIsShowMessageSearchResult(false);
		setSearchText("");
	};

	const handleSearchCompleted = () => {
		setIsSearchInputOpened(false);
		setSearchText("");
		setIsShowMessageSearchResult(false);
	};

	const handleCloseSearchUI = () => {
		setIsSearchInputOpened(false);
		setIsShowMessageSearchResult(false);
	};

	return (
		<StyledTabContent activeId={roomID}>
			<Maybe test={isSearchInputOpened && roomID}>
				<Row
					verticalAlign="center"
					gap={8}
					style={{
						width: "100%",
						padding: "4px 60px 4px 60px",
						backgroundColor:
							isShowMessageSearchResult && Boolean(searchText.trim())
								? colors.accent_10_100
								: colors.neutral_10_100
					}}
				>
					<MessageSearchInConversation
						searchedText={searchText}
						onClear={handleOnClearSearchInput}
						roomId={roomID}
					/>
					<Row gap={8} verticalAlign="center" style={{ paddingRight: 8 }}>
						<Button.Icon
							shape="round"
							name="Close24"
							size="medium_18"
							variant="text"
							onClick={handleCloseSearchUI}
						/>
					</Row>
				</Row>
			</Maybe>

			<Maybe test={!isShowMessageSearchResult && !isSearchInputOpened}>
				<Row style={{ width: "100%", padding: "8px 60px" }} horizonAlign="center">
					<VoiceCallLayer chatSize="full" roomID={channelData.roomId} spaceID={channelData.spaceId} />
				</Row>
			</Maybe>

			<Maybe test={channelData.roomJoined} fallback={<UnauthorizedChannel createdBy={channelData.createdBy} />}>
				{isShowMessageSearchResult && Boolean(searchText.trim()) ? (
					<SearchedResultWrapper>
						<MessageSearchResult
							onComplete={handleSearchCompleted}
							searchText={searchText}
							roomID={roomID}
						/>
					</SearchedResultWrapper>
				) : (
					<MessageList
						isMyRoom={channelData.myRoom}
						variant="fullSize"
						roomID={roomID}
						spaceID={spaceID}
						onShowRecentMessage={handleShowRecentMessage}
						notice={channelData.notice}
						voteNotice={channelData.voteNotice}
						activityMessageID={activityMessageID}
					/>
				)}
			</Maybe>

			<Maybe test={channelData.private && channelData.deleted && channelData.roomJoined}>
				<DeletedChannel roomID={channelData.roomId} />
			</Maybe>

			<Maybe test={channelData.roomJoined && !isShowMessageSearchResult}>
				<MessageInput
					id={currentTabID || ""}
					type="Space"
					disabled={channelData.private && channelData.deleted && channelData.roomJoined}
					userList={
						channelData.users.map((v) => ({ userID: v.id, name: v.name, src: v.profileImagePath })) || []
					}
					variant="fullSize"
					spaceID={spaceID}
					myRoomID={channelData.myRoomId}
					isMyRoom={channelData.myRoom}
				/>
			</Maybe>
		</StyledTabContent>
	);
}

const StyledTabContent = styled(TabContent)`
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
`;
